import React, { useContext, useEffect, useState } from "react";
import { Modal, ModalBody, ModalHeader, Spinner, Input } from "reactstrap";
import { Button, Icon } from "../../../components/Component";
import { igGetGreetingConfig, igUpdateGreetingConfig } from "../../../services/igAdapter";
import { IgChatContext } from "./IgChatContext";

/**
 * IgChatModalGreeting — modal pengaturan greeting otomatis per akun IG.
 *
 * Props:
 *   isOpen: boolean
 *   onClose: function()
 */
const IgChatModalGreeting = ({ isOpen, onClose }) => {
  const { accountListState } = useContext(IgChatContext);
  const [accountList] = accountListState;

  const [selectedSlug, setSelectedSlug] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [message, setMessage] = useState("");
  const [cooldownHours, setCooldownHours] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setSaved(false);
    const firstSlug = accountList?.[0]?.slug || "";
    setSelectedSlug(firstSlug);
    if (firstSlug) {
      fetchConfig(firstSlug);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchConfig = async (slug) => {
    setIsLoading(true);
    setSaved(false);
    const res = await igGetGreetingConfig(slug);
    if (res?.data) {
      setEnabled(!!res.data.enabled);
      setMessage(res.data.message || "");
      setCooldownHours(res.data.cooldown_hours ?? 0);
    } else {
      setEnabled(false);
      setMessage("");
      setCooldownHours(0);
    }
    setIsLoading(false);
  };

  const handleSelectAccount = (e) => {
    const slug = e.target.value;
    setSelectedSlug(slug);
    if (slug) {
      fetchConfig(slug);
    }
  };

  const handleSave = async () => {
    if (!selectedSlug) return;
    setIsSaving(true);
    setSaved(false);
    const res = await igUpdateGreetingConfig(selectedSlug, enabled, message, cooldownHours);
    setIsSaving(false);
    if (res?.data) {
      setEnabled(!!res.data.enabled);
      setMessage(res.data.message || "");
      setCooldownHours(res.data.cooldown_hours ?? 0);
      setSaved(true);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="md" className="modal-dialog-centered">
      <ModalHeader toggle={onClose}>Pengaturan Greeting Otomatis</ModalHeader>
      <ModalBody>
        {/* Testing notice */}
        <div className="alert alert-warning alert-icon mb-3" style={{ fontSize: 13 }}>
          <Icon name="alert-circle" />
          <strong>Fitur dalam tahap testing</strong> — Fitur greeting otomatis belum dipublikasikan secara resmi.
          Gunakan dengan hati-hati.
        </div>

        {/* Account selector */}
        <div className="form-group mb-3">
          <label className="form-label">Akun Instagram</label>
          <div className="form-control-wrap">
            <select className="form-select form-control" value={selectedSlug} onChange={handleSelectAccount}>
              {(accountList || []).map((acc) => (
                <option key={acc.slug} value={acc.slug}>
                  {acc.name || acc.slug}
                </option>
              ))}
            </select>
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-3">
            <Spinner size="sm" />
          </div>
        ) : (
          <>
            {/* Enable toggle */}
            <div className="form-group mb-3">
              <div className="custom-control custom-switch">
                <input
                  type="checkbox"
                  className="custom-control-input"
                  id="ig-greeting-enabled"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                />
                <label className="custom-control-label" htmlFor="ig-greeting-enabled">
                  Aktifkan greeting otomatis
                </label>
              </div>
            </div>

            {/* Cooldown interval */}
            <div className="form-group mb-3">
              <label className="form-label">Kirim ulang greeting setiap</label>
              <select
                className="form-select form-control"
                value={cooldownHours}
                onChange={(e) => setCooldownHours(Number(e.target.value))}
              >
                <option value={0}>Sekali saja (tidak diulang)</option>
                <option value={1}>Setiap 1 jam</option>
                <option value={3}>Setiap 3 jam</option>
                <option value={6}>Setiap 6 jam</option>
                <option value={12}>Setiap 12 jam</option>
                <option value={24}>Setiap 24 jam</option>
                <option value={48}>Setiap 48 jam</option>
              </select>
              <small className="text-muted">
                {cooldownHours === 0
                  ? "Greeting hanya dikirim saat percakapan pertama dimulai."
                  : `Greeting dikirim ulang jika customer pesan lagi setelah ${cooldownHours} jam.`}
              </small>
            </div>

            {/* Message */}
            <div className="form-group mb-3">
              <label className="form-label">Pesan Greeting</label>
              <Input
                type="textarea"
                rows={4}
                placeholder="Tulis pesan sambutan untuk chat baru..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>
          </>
        )}

        {/* Actions */}
        <div className="w-full d-flex justify-content-end align-items-center mt-4">
          {saved && <span className="text-success mr-2" style={{ fontSize: 13 }}>Tersimpan</span>}
          <Button color="primary" disabled={!selectedSlug || isLoading || isSaving} onClick={handleSave}>
            {isSaving ? <Spinner size="sm" /> : "Simpan"}
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default IgChatModalGreeting;
